//src/lib/onboardingUtils.ts
export const VerificationStage = {
  WELCOME: "welcome",
  CONSENT: "consent",
  SELECT_ID_TYPE: "select_id_type",
  UPLOAD_DOCUMENT: "upload_document",
  TAKE_SELFIE: "take_selfie",
  PROOF_OF_ADDRESS: "proof_of_address",
  PROOF_OF_FUNDS: "proof_of_funds",
  VERIFICATION_PROGRESS: "verification_progress",
  VERIFICATION_PENDING: "verification_pending",
  VERIFICATION_SUCCESS: "verification_success",
  VERIFICATION_MANUAL_REVIEW: "verification_manual_review",
  VERIFICATION_FAILED: "verification_failed",
  CHANGE_CHECK: "change_check",
  ADDITIONAL_DATA: "additional_data",
  REVIEW_DETAILS: "review_details",
  COMPLETE: "complete",
  MODIFY_DETAILS: "modify_details",
  DOCUSIGN: "docusign",
  SIGNATURE_PENDING: "Signature Pending",
  SIGNATURE_BLOCKED: "Signature Blocked",
  PAYMENT_PENDING: "Payment Pending",
  PAYMENT_BLOCKED: "Payment Blocked",
  PAYMENT_COMPLETE: "Payment completed",
    ONBOARDING_COMPLETE: "Onboarding completed",
  BLOCKED: "blocked",
};

export const generateMessageId = (prefix = "msg") =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;

export const createMessage = (content, isUser, options = {}) => {
  const prefix = isUser
    ? "user"
    : options?.actionType === "upload"
      ? `upload-${options.attachmentType}`
      : options?.actionType === "reminder"
        ? "reminder"
        : options?.actionType === "form"
          ? "form"
          : "ai";

  return {
    id: generateMessageId(prefix),
    content,
    isUser,
    timestamp: new Date(),
    isDisabled: false,
    ...options,
  };
};

export const checkJourneyStepResult = (
  verificationResult,
  expectedStepType,
) => {
  try {
    const metadata =
      typeof verificationResult.metadata === "string"
        ? JSON.parse(verificationResult.metadata)
        : verificationResult.metadata;

    const originalResponse = metadata?.originalResponse;
    if (!originalResponse || !originalResponse.JourneySteps) {
      return { passed: false, reason: "No journey step data available" };
    }

    const journeyStep = originalResponse.JourneySteps.find(
      (step) => step.Type === expectedStepType,
    );

    if (!journeyStep) {
      return {
        passed: false,
        reason: `No ${expectedStepType} step found in journey`,
      };
    }

    const stepResult =
      journeyStep.HighLevelResult || journeyStep.HighLevelresult;

    if (expectedStepType === "SELFIE") {
      const additionalData = originalResponse.AdditionalData || [];
      const faceMatchResult = additionalData.find(
        (item) => item.Name === "AutomatedFaceMatchResult",
      );
      const faceMatchScore = additionalData.find(
        (item) => item.Name === "FaceMatchScore",
      );

      if (faceMatchResult?.Value === "PASSED") {
        return { passed: true, reason: null };
      }

      if (
        journeyStep.FailureReason?.includes("Facematch") ||
        journeyStep.FailureReason?.includes("No Facematch")
      ) {
        return {
          passed: false,
          reason:
            "Face matching failed. Please ensure good lighting and look directly at the camera.",
        };
      }

      const stepPassed = stepResult === "Passed";

      if (!stepPassed) {
        return {
          passed: false,
          reason: journeyStep.FailureReason || "Selfie validation failed",
        };
      }

      return { passed: true, reason: null };
    } else {
      const stepPassed = stepResult === "Passed" || stepResult === "Refer";

      if (!stepPassed) {
        const failureReasons =
          journeyStep.ValidationChecks?.filter((check) =>
            check.includes("FAILED"),
          ) || [];

        let reason =
          journeyStep.FailureReason || `${expectedStepType} validation failed`;

        if (failureReasons.length > 0) {
          reason = failureReasons.join(", ");
        }

        return {
          passed: false,
          reason: reason,
        };
      }

      return { passed: true, reason: null };
    }
  } catch (error) {
    console.error(`Error checking journey step result:`, error);
    return { passed: false, reason: "Error validating document" };
  }
};

export const extractPersonalDetails = (verificationResult) => {
  try {
    if (!verificationResult || !verificationResult.metadata) {
      return null;
    }

    const metadata =
      typeof verificationResult.metadata === "string"
        ? JSON.parse(verificationResult.metadata)
        : verificationResult.metadata;

    return {
      personalDetails: metadata?.extractedData?.personalDetails || null,
      documentDetails: metadata?.extractedData?.documentDetails || null,
      addressDetails: metadata?.extractedData?.addressDetails || null,
    };
  } catch (error) {
    console.error("Error parsing verification metadata:", error);
    return null;
  }
};

export const formatPersonalDetails = (
  extractedData,
  sortCode = "",
  accountNumber = "",
) => {
  if (!extractedData) return "Details not available";

  let formattedDetails = "";

  if (extractedData.personalDetails) {
    formattedDetails += "<div><strong>Personal Details:</strong></div>";
    const personal = extractedData.personalDetails;
    if (personal.fullName)
      formattedDetails += `• Full Name: ${personal.fullName}\n`;
    if (personal.dateOfBirth)
      formattedDetails += `• Date of Birth: ${personal.dateOfBirth}\n`;
    if (personal.nationality)
      formattedDetails += `• Nationality: ${personal.nationality}\n`;
    if (personal.gender) formattedDetails += `• Gender: ${personal.gender}\n`;
    formattedDetails += "\n";
  }

  if (extractedData.documentDetails) {
    formattedDetails += "<div><strong>Document Details:</strong></div>";
    const doc = extractedData.documentDetails;
    if (doc.documentType)
      formattedDetails += `• Document Type: ${doc.documentType}\n`;
    if (doc.documentNumber)
      formattedDetails += `• Document Number: ${doc.documentNumber}\n`;
    if (doc.issueDate) formattedDetails += `• Issue Date: ${doc.issueDate}\n`;
    if (doc.expiryDate)
      formattedDetails += `• Expiry Date: ${doc.expiryDate}\n`;
    if (doc.issuingCountry)
      formattedDetails += `• Issuing Country: ${doc.issuingCountry}\n`;
    formattedDetails += "\n";
  }

  if (extractedData.addressDetails) {
    formattedDetails += "<div><strong>Addres Details:</strong></div>";
    const addr = extractedData.addressDetails;
    if (addr.fullAddress)
      formattedDetails += `• Full Address: ${addr.fullAddress}\n`;
    if (addr.city) formattedDetails += `• City: ${addr.city}\n`;
    if (addr.postalCode)
      formattedDetails += `• Postal Code: ${addr.postalCode}\n`;
    if (addr.country) formattedDetails += `• Country: ${addr.country}\n`;
    formattedDetails += "\n";
  }

  formattedDetails += "<div><strong>Banking Details:</strong></div>";

  const formattedSortCode =
    sortCode.length === 6 && !sortCode.includes("-")
      ? `${sortCode.slice(0, 2)}-${sortCode.slice(2, 4)}-${sortCode.slice(4, 6)}`
      : sortCode;

  formattedDetails += `• Sort Code: ${formattedSortCode}\n`;

  const formattedAccountNumber = accountNumber.replace(
    /(\d{4})(\d{4})/,
    "$1 $2",
  );
  formattedDetails += `• Account Number: ${formattedAccountNumber}\n`;

  return formattedDetails;
};

export const verifyUserData = async (dataToVerify, hasChanges) => {
  console.log("Verifying user data:", dataToVerify, "Has changes:", hasChanges);

};
